
import React from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Home, PlusCircle, Settings, User, Users, UserCog, Bell, LogOut, Moon, Sun, ClipboardList, Globe, Package } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import useAuthStore from "../store/authStore";
import useThemeStore from "../store/themeStore";
import authApi from "../api/authApi";

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { mode, toggleTheme } = useThemeStore();

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch (e) {
      console.error("Logout error", e);
    } finally {
      logout();
      navigate("/auth");
    }
  };

  const baseNavItems = [
    { path: "/dashboard", icon: Home, label: "Tổng quan" },
    { path: "/repairs", icon: ClipboardList, label: "Danh sách" },
    { path: "/create", icon: PlusCircle, label: "Tạo phiếu" },
    { path: "/customers", icon: Users, label: "Khách hàng" },
    { path: "/inventory", icon: Package, label: "Kho linh kiện" },
  ];

  const navItems = user?.role === "Admin" 
    ? [...baseNavItems, { path: "/users", icon: UserCog, label: "Tài khoản" }]
    : baseNavItems;

  return (
    <div className="flex h-[100dvh] w-full overflow-hidden font-sans relative bg-transparent md:p-4 md:gap-4">
      
      {/* Sidebar for Desktop - Floating Island Style */}
      <aside className="hidden md:flex flex-col w-[280px] glass-panel rounded-[2rem] z-20 shadow-[0_8px_32px_rgba(0,0,0,0.04)] border border-white/60">
        <div className="p-7 flex items-center space-x-4 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-400 to-primary-600 shadow-lg shadow-primary-500/30 flex items-center justify-center text-white font-black text-2xl border-2 border-white/50">
            T
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-slate-800 tracking-tight leading-tight">TechManage</h1>
            <div>
              <span className="text-[10px] font-bold text-primary-500 uppercase tracking-widest bg-primary-50 px-2 py-0.5 rounded-full inline-block mt-1 mr-2">Sửa chữa</span>
            </div>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">By Vũ Bảo Software</p>
          </div>
        </div>

        <nav className="flex-1 px-5 mt-4 space-y-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || (item.path !== "/" && location.pathname.startsWith(item.path));
            const Icon = item.icon;
            return (
              <NavLink 
                key={item.path}
                to={item.path}
                className={"flex items-center space-x-4 px-5 py-4 rounded-2xl transition-all duration-300 relative group overflow-hidden " + (isActive ? "text-primary-700 font-bold" : "text-slate-500 hover:text-slate-700")}
              >
                {!isActive && (
                  <div className="absolute inset-0 bg-slate-400/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300 rounded-2xl z-0" />
                )}
                {isActive && (
                  <motion.div 
                    layoutId="desktopActiveTab"
                    className="absolute inset-0 bg-gradient-to-r from-primary-100/80 to-primary-50/40 border border-primary-200/50 rounded-2xl z-0 shadow-sm"
                    transition={{ type: "spring", stiffness: 350, damping: 25 }}
                  />
                )}
                <Icon size={22} className={"z-10 transition-transform " + (isActive ? "scale-110 drop-shadow-sm text-primary-600" : "group-hover:scale-110")} strokeWidth={isActive ? 2.5 : 2} />
                <span className="z-10 tracking-tight text-[15px]">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        
        <div className="p-6 m-4 bg-white/40 rounded-3xl border border-white/50 shadow-sm flex justify-between items-center backdrop-blur-md">
          <NavLink to="/profile" className="flex items-center space-x-3 cursor-pointer hover:bg-white/60 p-2 -ml-2 rounded-2xl transition-colors w-full">
            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-slate-200 to-white border-2 border-white shadow-sm flex items-center justify-center font-bold text-slate-600 flex-shrink-0">
              {user?.fullName?.charAt(0)?.toUpperCase() || "A"}
            </div>
            <div className="overflow-hidden pr-2">
              <p className="text-sm font-bold text-slate-800 truncate">{user?.fullName || "Admin"}</p>
              <p className="text-[11px] font-semibold text-slate-500">{user?.role || "Quản lý"}</p>
            </div>
          </NavLink>
          <button onClick={handleLogout} title="Đăng xuất" className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors flex-shrink-0 ml-1">
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-full relative w-full max-w-[1600px] mx-auto overflow-hidden">
        
        {/* Mobile Header - Floating Pill */}
        <header className="md:hidden glass-panel z-20 fixed top-3 left-3 right-3 px-5 py-3 flex justify-between items-center rounded-2xl shadow-lg shadow-slate-200/20 border border-white/60">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 shadow-sm flex items-center justify-center text-white font-bold border-2 border-white">
              T
            </div>
            <div className="flex flex-col">
              <h1 className="text-base font-extrabold text-slate-900 tracking-tight leading-tight">TechManage</h1>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">By Vũ Bảo Software</p>
            </div>
          </div>
          <div className="flex items-center space-x-1 md:space-x-2">
            <NavLink to="/" className="p-1.5 md:p-2 text-primary-600 bg-primary-50 dark:bg-primary-900/40 rounded-full shadow-sm border border-primary-200 dark:border-primary-800/50 hover:scale-105 transition-transform" title="Về trang chủ khách hàng">
              <Globe size={18} />
            </NavLink>
            <button onClick={toggleTheme} className="p-1.5 md:p-2 text-slate-500 bg-white/80 dark:bg-slate-800/80 rounded-full shadow-sm border border-slate-100 dark:border-slate-700 transition-transform hover:scale-105">
              {mode === "dark" ? <Sun size={18} className="text-amber-400" /> : <Moon size={18} />}
            </button>
            <NavLink to="/profile" className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-white dark:bg-slate-800 shadow-sm flex items-center justify-center text-slate-700 dark:text-slate-200 font-bold border border-slate-100 dark:border-slate-700 hover:scale-105 transition-transform text-sm">
              {user?.fullName?.charAt(0)?.toUpperCase() || "A"}
            </NavLink>
            <button onClick={handleLogout} className="p-1.5 md:p-2 text-red-500 bg-red-50 dark:bg-red-500/10 rounded-full shadow-sm border border-red-100 dark:border-red-500/20 transition-transform hover:scale-105">
              <LogOut size={18} />
            </button>
          </div>
        </header>

        {/* Desktop Header - Floating Pill */}
        <header className="hidden md:flex glass-panel z-10 px-8 py-4 mb-6 rounded-3xl justify-between items-center shadow-sm border border-white/60">
          <h2 className="text-xl font-bold text-slate-800 tracking-tight">
            {navItems.find(item => item.path === location.pathname)?.label || "Bảng điều khiển"}
          </h2>
          <div className="flex items-center space-x-4">
            <NavLink to="/" className="flex items-center space-x-2 px-4 py-2 bg-primary-50 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300 rounded-xl font-bold text-sm shadow-sm border border-primary-200 dark:border-primary-800/50 hover:shadow-md hover:-translate-y-0.5 transition-all">
              <Globe size={18} />
              <span>Xem cửa hàng</span>
            </NavLink>
            <button onClick={toggleTheme} className="p-2.5 text-slate-500 hover:text-primary-600 bg-white/80 dark:bg-slate-800/80 rounded-full shadow-sm border border-white dark:border-slate-700 transition-all hover:scale-105 hover:shadow-md">
              {mode === "dark" ? <Sun size={20} className="text-amber-400" /> : <Moon size={20} />}
            </button>
            <button className="p-2.5 text-slate-500 hover:text-primary-600 bg-white/80 dark:bg-slate-800/80 rounded-full shadow-sm border border-white dark:border-slate-700 transition-all hover:scale-105 hover:shadow-md">
              <Bell size={20} />
            </button>
          </div>
        </header>

        {/* Scrollable Main */}
        {/* Mobile needs extra padding top for the floating header, and extra padding bottom for the floating nav */}
        <main className="flex-1 overflow-y-auto hide-scrollbar relative pt-24 pb-40 md:pt-0 md:pb-8 px-4 md:px-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 15, scale: 0.99 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -15, scale: 0.99 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="min-h-full max-w-5xl mx-auto"
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* Mobile Bottom Navigation - Floating Pill */}
      <nav className="md:hidden glass-panel fixed bottom-4 left-2 right-2 px-1 py-2 rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.1)] z-50 border border-white/60">
        <ul className="flex justify-between items-center">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || (item.path !== "/" && location.pathname.startsWith(item.path));
            const Icon = item.icon;
            
            return (
              <li key={item.path} className="relative flex-1">
                <NavLink 
                  to={item.path}
                  className={"flex flex-col items-center py-2 px-0.5 rounded-2xl transition-all duration-300 ease-out " + (isActive ? "text-primary-600" : "text-slate-400")}
                >
                  <motion.div
                    animate={{ y: isActive ? -2 : 0, scale: isActive ? 1.15 : 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    <Icon size={22} strokeWidth={isActive ? 2.5 : 2} className={isActive ? "drop-shadow-sm" : ""} />
                  </motion.div>
                  <span className={"text-[9px] sm:text-[10px] mt-1 font-bold whitespace-nowrap transition-all duration-300 " + (isActive ? "opacity-100" : "opacity-0 h-0 overflow-hidden")}>
                    {item.label}
                  </span>
                  
                  {isActive && (
                    <motion.div 
                      layoutId="mobileActiveTab"
                      className="absolute -bottom-1 w-1.5 h-1.5 bg-primary-600 rounded-full"
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    />
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default Layout;

